/**
 * ContactEditModal Component
 * Modal untuk edit nama pemesan dan nomor handphone
 */

import {
  Box,
  Flex,
  FormLabel,
  Grid,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { Button, Text, TextField } from '@diamond/shared/ui';
import { useFormContext } from 'react-hook-form';

export interface ContactEditModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * ContactEditModal Component
 * Modal untuk edit contact person (nama dan nomor HP)
 */
export function ContactEditModal({ isOpen, onClose }: ContactEditModalProps) {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  /**
   * Handle save (just close modal, form auto-updates)
   */
  const handleSave = () => {
    // Validation will be handled by form
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text fontSize="xl" fontWeight="bold">
            Kontak Pemesan
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Grid
            templateColumns={{ base: '1fr', md: '1fr 1fr' }}
            gap={4}
            alignItems="flex-start"
          >
            {/* Nama Field */}
            <Box>
              <FormLabel htmlFor="ship_to_contact.name" mb={2}>
                <Text fontWeight="medium">Nama</Text>
              </FormLabel>
              <TextField
                id="ship_to_contact.name"
                name="ship_to_contact.name"
                placeholder="Tulis nama penerima"
                size="md"
                rounded="md"
                register={register}
                registerOptions={{
                  required: 'Nama pemesan wajib diisi',
                  minLength: {
                    value: 2,
                    message: 'Nama minimal 2 karakter',
                  },
                }}
                errors={errors}
              />
            </Box>

            {/* Nomor Handphone Field */}
            <Box>
              <FormLabel htmlFor="ship_to_contact.telp" mb={2}>
                <Text fontWeight="medium">Nomor Handphone</Text>
              </FormLabel>
              <TextField
                id="ship_to_contact.telp"
                name="ship_to_contact.telp"
                placeholder="+6281234567890"
                size="md"
                rounded="md"
                type="tel"
                maxLength={15}
                register={register}
                registerOptions={{
                  required: 'Nomor HP wajib diisi',
                  pattern: {
                    value: /^\+?\d+$/,
                    message: 'Nomor HP hanya boleh berisi angka',
                  },
                  minLength: {
                    value: 10,
                    message: 'Nomor HP minimal 10 digit',
                  },
                  maxLength: {
                    value: 15,
                    message: 'Nomor HP maksimal 14 digit (+ opsional di depan)',
                  },
                }}
                errors={errors}
                onInput={(e) => {
                  const input = e.currentTarget as HTMLInputElement;
                  // Allow optional leading '+' but keep the payload clean via normalization on submit.
                  const raw = input.value.replace(/[^\d+]/g, '');
                  input.value = raw.startsWith('+')
                    ? `+${raw.slice(1).replace(/\+/g, '')}`
                    : raw.replace(/\+/g, '');
                }}
              />
            </Box>
          </Grid>
        </ModalBody>

        <ModalFooter>
          <Flex w="full" justify="flex-end" gap={3}>
            <Button variant="ghost" onClick={onClose}>
              Batal
            </Button>
            <Button variant="solid" onClick={handleSave}>
              Simpan
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
